import { useState } from 'react';
import { t, tn } from '@app/i18n';
import { Controller, useForm } from 'react-hook-form';
import {
  Alert,
  Badge,
  Card,
  ErrorBanner,
  Field,
  FieldGrid,
  LoadingButton,
  TextInput,
} from '@zarya/ui';
import type { ApiError } from '@app/api/ApiError';
import { useCanOperar } from '@app/auth/useCan';
import { sweepErrorPhrase } from '@app/discovery';
import { dataHour } from '@app/format';
import { DiscoveryNowButton } from '../Identities/DiscoveryNowButton';
import { useDiscoveryConfig, useUpdateDiscoveryConfig } from './TenantSettings.api';
import {
  discoveryDefaults,
  forUpdateDiscovery,
  type DiscoveryConfig,
  type DiscoveryFormValues,
} from './TenantSettingsModel';
import { useDiscoveryValidation } from './useFormValidation';
import s from './TenantSettings.module.css';

/**
 * Card **Discovery** de T21 (FR-039, FR-117) — US14, T426.
 *
 * Estado ativo/pausado, intervalo em minutos (≥ 1, padrão 5), última varredura
 * com resultado ou erro, e **Executar agora** (o mesmo botão de T3/T4). Salvar é
 * local — o worker relê a configuração a cada tick; não há ordem ao middleware.
 */
export function DiscoveryCard() {
  const config = useDiscoveryConfig();
  // A mutação mora no card, não no formulário: o formulário é recriado (`key`)
  // quando a configuração muda — justamente depois de salvar — e o alerta de
  // sucesso morreria junto com ele.
  const save = useUpdateDiscoveryConfig();
  const [erroDaExecucao, setErroDaExecucao] = useState<ApiError | null>(null);
  const [avisoDaExecucao, setAvisoDaExecucao] = useState<string | null>(null);

  return (
    <Card
      title={t('tenantSettings.discovery.title')}
      subtitle={t('tenantSettings.discovery.subtitle')}
      toolbar={
        config.data ? (
          config.data.running ? (
            <Badge tone="primary">{t('tenantSettings.discovery.badge.running')}</Badge>
          ) : config.data.active ? (
            <Badge tone="success">{t('tenantSettings.discovery.badge.active')}</Badge>
          ) : (
            <Badge tone="warning">{t('tenantSettings.discovery.badge.paused')}</Badge>
          )
        ) : null
      }
    >
      {config.isError ? (
        <ErrorBanner
          error={config.error}
          title={t('tenantSettings.discovery.error.read')}
          onRetry={() => void config.refetch()}
          hideLink
        />
      ) : null}

      {config.isLoading ? (
        <p className={s.carregando}>{t('tenantSettings.discovery.loading')}</p>
      ) : null}

      {save.error ? (
        <ErrorBanner
          error={save.error}
          escrita
          title={t('tenantSettings.discovery.error.save')}
          hideLink
        />
      ) : null}
      {save.isSuccess && !save.isPending ? (
        <Alert
          tone="info"
          title={t(
            save.data.active
              ? 'tenantSettings.discovery.saved.active'
              : 'tenantSettings.discovery.saved.paused',
          )}
        />
      ) : null}

      {config.data ? (
        <>
          <DiscoveryForm
            key={`${config.data.active}:${config.data.interval_min}`}
            config={config.data}
            pending={save.isPending}
            onSubmit={(valores) => save.mutate(forUpdateDiscovery(valores))}
          />

          <LastSweep config={config.data} />

          <div className={s.acoes}>
            <DiscoveryNowButton onErro={setErroDaExecucao} onAviso={setAvisoDaExecucao} />
          </div>

          {erroDaExecucao ? (
            <ErrorBanner
              error={erroDaExecucao}
              escrita
              title={t('tenantSettings.discovery.run.error')}
            />
          ) : null}
          {avisoDaExecucao ? (
            <Alert tone="warning" title={t('tenantSettings.discovery.run.warning')}>
              {avisoDaExecucao}
            </Alert>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}

function DiscoveryForm({
  config,
  pending,
  onSubmit,
}: {
  config: DiscoveryConfig;
  pending: boolean;
  onSubmit: (valores: DiscoveryFormValues) => void;
}) {
  const canSave = useCanOperar('updateDiscoveryConfig');
  const { control, handleSubmit, formState } = useForm<DiscoveryFormValues>({
    defaultValues: discoveryDefaults(config),
    ...useDiscoveryValidation(),
  });

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      aria-label={t('tenantSettings.discovery.form.label')}
    >
      <FieldGrid>
        <Controller
          control={control}
          name="ativo"
          render={({ field }) => (
            <Field
              label={t('tenantSettings.discovery.form.state')}
              span={6}
              tip={t('tenantSettings.discovery.form.state.tip')}
            >
              {(props) => (
                <span className={s.toggle}>
                  <input
                    id={props.id}
                    type="checkbox"
                    role="switch"
                    aria-label={t('tenantSettings.discovery.form.state')}
                    aria-checked={field.value}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={!canSave}
                  />
                  <span data-testid="estado-discovery">
                    {t(
                      field.value
                        ? 'tenantSettings.discovery.form.state.active'
                        : 'tenantSettings.discovery.form.state.paused',
                    )}
                  </span>
                </span>
              )}
            </Field>
          )}
        />

        <Controller
          control={control}
          name="intervaloMin"
          render={({ field, fieldState }) => (
            <Field
              label={t('tenantSettings.discovery.form.interval')}
              span={6}
              required
              error={fieldState.error?.message}
              tip={t('tenantSettings.discovery.form.interval.tip')}
            >
              {(props) => (
                <TextInput
                  {...props}
                  name={field.name}
                  ref={field.ref}
                  onBlur={field.onBlur}
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  value={Number.isNaN(field.value) ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? NaN : Number(e.target.value))
                  }
                  disabled={!canSave}
                />
              )}
            </Field>
          )}
        />
      </FieldGrid>

      {canSave ? (
        <div className={s.acoes}>
          <LoadingButton type="submit" loading={pending} disabled={!formState.isValid}>
            {t('tenantSettings.discovery.form.save')}
          </LoadingButton>
        </div>
      ) : null}
    </form>
  );
}

/**
 * As três contagens da varredura, cada uma com plural próprio.
 *
 * Eram interpolação crua (`5 imagens varridas`), e no singular saíam «1 imagens
 * varridas». O `tn()` resolve as duas formas; o zero segue plural, como no resto
 * do console («0 imagens varridas»).
 */
function contagens(resultado: NonNullable<DiscoveryConfig['last_result']>): string {
  return [
    tn('tenantSettings.discovery.lastSweep.images', resultado.images_scanned ?? 0),
    tn('tenantSettings.discovery.lastSweep.releases', resultado.new_releases ?? 0),
    tn('tenantSettings.discovery.lastSweep.queued', resultado.queued ?? 0),
  ].join(' · ');
}

/**
 * « — <frase do catálogo>» depois do código de uma varredura que falhou, ou vazio quando o código não tem frase
 * própria (aim-jgy). A frase sai do catálogo pelo código, nos dois idiomas; a `error_message` do resultado é a
 * mensagem do domínio, em português, e não vai à tela.
 */
function failureDetail(code: string): string {
  const phrase = sweepErrorPhrase(code);
  return phrase ? t('tenantSettings.discovery.lastSweep.failed.detail', { message: phrase }) : '';
}

/** "Última varredura": resultado (contagens) ou o erro da execução (FR-117). */
function LastSweep({ config }: { config: DiscoveryConfig }) {
  const resultado = config.last_result;
  return (
    <div className={s.conexao} data-testid="ultima-varredura">
      <div className={s.conexaoTexto}>
        <h4>{t('tenantSettings.discovery.lastSweep.title')}</h4>
        <p className={s.meta}>
          {config.last_execution_at
            ? dataHour(config.last_execution_at)
            : t('tenantSettings.discovery.lastSweep.never')}
          {resultado
            ? resultado.error_code
              ? `${t('tenantSettings.discovery.lastSweep.failed', {
                  code: resultado.error_code,
                })}${failureDetail(resultado.error_code)}`
              : ` · ${contagens(resultado)}`
            : ''}
        </p>
        {/* **Todos** os avisos, um por item (Lote 2b). Antes T21 não mostrava
            nenhum: a varredura podia fechar com dois avisos e a tela só dizia as
            contagens. O texto é dado do servidor (`warnings[]` é `[]string` livre) e
            sai cru; do catálogo vem só o título com a contagem. */}
        {resultado?.warnings?.length ? (
          <Alert
            tone="warning"
            title={tn('tenantSettings.discovery.lastSweep.warnings', resultado.warnings.length)}
          >
            <ul className={s.avisos}>
              {resultado.warnings.map((aviso) => (
                <li key={aviso}>{aviso}</li>
              ))}
            </ul>
          </Alert>
        ) : null}
      </div>
    </div>
  );
}
